import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from '../components/layout/PageLayout';
import { formatDate } from '../utils/helpers';
import instituteService from '../services/instituteService';
import { getListingCoverImageUrl } from '../utils/imageFallback';
import ListingCoverHero from '../components/common/ListingCoverHero';
import ClickableImageGrid from '../components/common/ClickableImageGrid';
import useImageLightbox from '../hooks/useImageLightbox';
import { normalizeGalleryImages } from '../utils/imageLightbox';
import {
  canManageInstitute,
  getInstituteSocietyName,
} from '../utils/institutePermissions';
import GoogleMapsLocationLink from '../components/common/GoogleMapsLocationLink';

const INSTITUTE_TYPE_LABELS = {
  school: 'School',
  college: 'College',
  university: 'University',
  training: 'Training Center',
  tuition: 'Tuition Center',
  other: 'Other',
};

const formatInstituteType = (type) =>
  INSTITUTE_TYPE_LABELS[type] || (type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Institute');

const formatInstituteLocation = (institute) => {
  const parts = [institute?.address, institute?.city].filter(Boolean);
  return parts.join(', ');
};

const InstituteDetailPage = () => {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const instituteId = params.id;
  const [institute, setInstitute] = useState(null);
  const [relatedInstitutes, setRelatedInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [views, setViews] = useState(0);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [message, setMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const { openLightbox, Lightbox } = useImageLightbox();

  useEffect(() => {
    if (instituteId) {
      loadInstitute();
    }
  }, [instituteId]);

  const loadInstitute = async () => {
    try {
      setLoading(true);
      const data = await instituteService.getInstitute(instituteId);
      setInstitute(data);
      setViews(data.views || 0);
      try {
        const societyId =
          typeof data.society === 'object' ? data.society?.id : data.society;
        const related = await instituteService.getRelatedInstitutes(instituteId, {
          society: societyId,
        });
        setRelatedInstitutes(related.results || related);
      } catch (relatedErr) {
        console.error('Error loading related institutes:', relatedErr);
      }
    } catch (err) {
      console.error('Error loading institute:', err);
      setError('Failed to load institute details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!canManageInstitute(user, institute)) {
      return;
    }

    if (window.confirm('Are you sure you want to delete this institute?')) {
      try {
        await instituteService.deleteInstitute(instituteId);
        navigate('/institutes');
      } catch (err) {
        console.error('Error deleting institute:', err);
        alert('Failed to delete institute');
      }
    }
  };

  const handleEdit = () => {
    if (!canManageInstitute(user, institute)) {
      return;
    }
    navigate(`/institutes/${instituteId}/edit`);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;

    setSendingMessage(true);
    try {
      console.log('Sending message:', message);
      setMessage('');
      setShowMessageForm(false);
      alert('Message sent successfully!');
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const getInstituteTypeColor = (type) => {
    const colors = {
      school: 'bg-blue-100 text-blue-800',
      college: 'bg-green-100 text-green-800',
      university: 'bg-purple-100 text-purple-800',
      training: 'bg-orange-100 text-orange-800',
      tuition: 'bg-amber-100 text-amber-800',
      other: 'bg-gray-100 text-gray-800',
    };
    return colors[type] || colors.other;
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
            <p className="mt-4 text-gray-600">Loading institute details...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error || !institute) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Institute Not Found</h1>
            <p className="text-gray-600 mb-6">{error || 'The institute you are looking for does not exist.'}</p>
            <button
              type="button"
              onClick={() => navigate('/institutes')}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
            >
              Back to Institutes
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  const canManage = canManageInstitute(user, institute);
  const societyName = getInstituteSocietyName(institute);
  const locationLine = formatInstituteLocation(institute);
  const galleryImages =
    institute.images?.length > 0
      ? institute.images
      : institute.display_image
        ? [institute.display_image]
        : [];
  const websiteHref = institute.website
    ? institute.website.startsWith('http')
      ? institute.website
      : `https://${institute.website}`
    : null;

  return (
    <PageLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-3">
              <div className="bg-white rounded-lg shadow-sm border overflow-hidden mb-6">
                <ListingCoverHero
                  src={getListingCoverImageUrl(institute)}
                  alt={institute.name}
                  onClick={
                    galleryImages.length > 0
                      ? () => openLightbox(normalizeGalleryImages(galleryImages, institute.name), 0)
                      : undefined
                  }
                />
                <div className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <h1 className="text-3xl font-bold text-gray-900">{institute.name}</h1>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getInstituteTypeColor(institute.institute_type)}`}>
                        {formatInstituteType(institute.institute_type)}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
                      <div className="flex items-center gap-1">
                        <span>{societyName || 'Unknown Society'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span>{views} views</span>
                      </div>
                      {institute.created_at && (
                        <div className="flex items-center gap-1">
                          <span>Posted {formatDate(institute.created_at)}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-4 mb-4">
                      {institute.phone && (
                        <div className="flex items-center gap-2 text-blue-600">
                          <a href={`tel:${institute.phone}`} className="hover:underline">
                            {institute.phone}
                          </a>
                        </div>
                      )}
                      {institute.contact_email && (
                        <div className="flex items-center gap-2 text-blue-600">
                          <a href={`mailto:${institute.contact_email}`} className="hover:underline">
                            {institute.contact_email}
                          </a>
                        </div>
                      )}
                      {websiteHref && (
                        <div className="flex items-center gap-2 text-blue-600">
                          <a href={websiteHref} target="_blank" rel="noopener noreferrer" className="hover:underline">
                            Website
                          </a>
                        </div>
                      )}
                      {locationLine && (
                        <div className="flex flex-col gap-1 text-gray-600">
                          <span>{locationLine}</span>
                          <GoogleMapsLocationLink
                            latitude={institute.latitude}
                            longitude={institute.longitude}
                            query={locationLine}
                            label="View on Map"
                          />
                        </div>
                      )}
                    </div>

                    {(institute.principal_name || institute.established_year || institute.total_students) && (
                      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm border-t border-gray-100 pt-4">
                        {institute.principal_name && (
                          <div>
                            <dt className="font-medium text-gray-500">Principal / Head</dt>
                            <dd className="mt-1 text-gray-900">{institute.principal_name}</dd>
                          </div>
                        )}
                        {institute.established_year && (
                          <div>
                            <dt className="font-medium text-gray-500">Established</dt>
                            <dd className="mt-1 text-gray-900">{institute.established_year}</dd>
                          </div>
                        )}
                        {institute.total_students != null && institute.total_students !== '' && (
                          <div>
                            <dt className="font-medium text-gray-500">Students</dt>
                            <dd className="mt-1 text-gray-900">{institute.total_students}</dd>
                          </div>
                        )}
                      </dl>
                    )}
                  </div>

                  {canManage && (
                    <div className="flex gap-2 mt-4 sm:mt-0">
                      <button
                        type="button"
                        onClick={handleEdit}
                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={handleDelete}
                        className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors duration-200"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
                </div>
              </div>

              <ClickableImageGrid
                images={galleryImages}
                title="Images"
                namePrefix={institute.name}
              />
              {Lightbox}

              <GoogleMapsLocationLink
                className="mb-6"
                variant="card"
                heading="Location"
                latitude={institute.latitude}
                longitude={institute.longitude}
                address={institute.address}
                city={institute.city}
                name={institute.name}
              />

              <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">About</h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {institute.description || 'No description available.'}
                </p>
              </div>

              {institute.facilities && (
                <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Facilities</h2>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{institute.facilities}</p>
                </div>
              )}

              {institute.admission_info && (
                <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Admission Information</h2>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{institute.admission_info}</p>
                </div>
              )}

              {!canManage && isAuthenticated && (
                <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Institute</h2>
                  {!showMessageForm ? (
                    <button
                      type="button"
                      onClick={() => setShowMessageForm(true)}
                      className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200"
                    >
                      Send Message
                    </button>
                  ) : (
                    <form onSubmit={handleSendMessage} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                        <textarea
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Type your message here..."
                          required
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          disabled={sendingMessage}
                          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
                        >
                          {sendingMessage ? 'Sending...' : 'Send Message'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowMessageForm(false)}
                          className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors duration-200"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {relatedInstitutes.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Related Institutes</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {relatedInstitutes.map((relatedInstitute) => (
                      <div
                        key={relatedInstitute.id}
                        onClick={() => navigate(`/institutes/${relatedInstitute.id}`)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            navigate(`/institutes/${relatedInstitute.id}`);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200 cursor-pointer"
                      >
                        <h3 className="font-semibold text-gray-900 mb-2">{relatedInstitute.name}</h3>
                        <p className="text-sm text-gray-600 mb-2">
                          {formatInstituteType(relatedInstitute.institute_type)}
                        </p>
                        <p className="text-sm text-gray-500">{relatedInstitute.society?.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default InstituteDetailPage;
